import { atom } from 'jotai';
import type { SectionColorRule } from './attribute-colors';
import type { WellFilter } from './well-filters';
import type {
  PickedWell,
  SectionMode,
  SectionVertex,
  SectionViewport,
  WellOrdering,
} from './types';
import type { CollectionGrid } from './use-structure-grid-collections';
import type { GroupedWell, RefineBaseline } from './utils';

/**
 * Local state for the Cross-Section tool: React Query owns whatever came from the
 * server, and everything here is what the user set. That split makes the PRD's rule —
 * display switches never recalculate — architectural rather than a thing to remember.
 */

// ── Arming and the plane ────────────────────────────────────────────────────

/**
 * True once the tool is armed: the drawer is open and the map is in section mode.
 * Arming happens when a method is picked, not when the button is pressed — the
 * button asks the question, the answer opens the drawer.
 */
export const crossSectionArmedAtom = atom(false);

/**
 * The prompt beside the arm button, which is where a section starts. Distinct from
 * an armed tool waiting on a method: that one asks the same question from inside the
 * drawer, because by then there is a drawer to ask it in.
 */
export const sectionModeMenuOpenAtom = atom(false);

/**
 * The chosen construction method, or null while the tool is armed but waiting for
 * one. Null is a real state, not an absence: the map takes no clicks until a mode
 * says what a click would mean — a vertex in one, a well in the other.
 */
export const sectionModeAtom = atom<SectionMode | null>(null);

/** The drawn polyline, in click order. Arbitrary Line only. */
export const sectionVerticesAtom = atom<SectionVertex[]>([]);

/**
 * Which section this is, as a counter: every reset — clear, mode switch, disarm, re-arm
 * — starts a new one. It lets the cut hook tell "editing this section", where the old
 * profile stays up, from "a new section", where it would be another line's answer.
 */
export const sectionGenerationAtom = atom(0);

/**
 * How many eligible wells the map is drawing, or null before it has been asked. Well to
 * Well tells the user to click wells, so a view holding none has to say so: an
 * instruction for a gesture that cannot land reads as a broken tool.
 */
export const eligibleWellsInViewAtom = atom<number | null>(null);

/**
 * The picked wells, in the order they were picked. Well to Well only.
 *
 * Pick order is not necessarily the traverse: the backend orders the plane and its
 * answer is what the section is drawn from. This is what was asked for.
 */
export const sectionWellsAtom = atom<PickedWell[]>([]);

/** How the traverse is ordered; falls back to as-selected once edited by hand. */
export const sectionOrderingAtom = atom<WellOrdering>('as_selected');

// ── The grid stack ──────────────────────────────────────────────────────────

/** The collection supplying the surfaces, or null before one is chosen. */
export const structureGridCollectionIdAtom = atom<number | null>(null);

/**
 * The Well Workspace whose own unpromoted geometry edits the cut carries (UAT 21).
 * Set by the geometry sheet's section alone; null everywhere else, so no other
 * section asks for a workspace's drafts.
 */
export const sectionWellWorkspaceIdAtom = atom<number | null>(null);

/**
 * Whether the cut may go out with no collection (UAT 30). Only the geometry sheet
 * sets it: a workspace there can have no collection at all, and its section still
 * shows the ground and the wells. The Map View keeps waiting for a collection.
 */
export const sectionCutsWithoutCollectionAtom = atom(false);

/**
 * Crossings the host adds beside the cut's own: the geometry sheet's unsaved edit,
 * plotted where the working line crosses the section (UAT 35). Empty everywhere else.
 */
export const hostCrossingsAtom = atom<GroupedWell[]>([]);

/**
 * The host's own colour per well, by ten-digit uwi: the geometry sheet colours the
 * section's wells the way its map does (UAT 36). Empty where the rail's rules rule.
 */
export const hostWellColorsAtom = atom<Record<string, string>>({});

/**
 * Whether the collection on the section was chosen by the user rather than picked for
 * them. An automatic choice is a guess and may be corrected once the line says which
 * collections it actually crosses; a deliberate one is an instruction and is not.
 */
export const collectionChosenByUserAtom = atom(false);

/**
 * Every grid the client knows the details of, by id. A section can carry a grid the
 * selected collection lacks, which then has no row to remove it by and no stored colour.
 * Accumulated rather than replaced: a grid outlives the collection that introduced it.
 */
export const knownGridsAtom = atom<Record<string, CollectionGrid>>({});

/**
 * Records what is known about some grids, keeping anything already recorded.
 *
 * @param grids - The grids just learned about.
 */
export const rememberGridsAtom = atom(
  null,
  (get, set, grids: CollectionGrid[]) => {
    const known = get(knownGridsAtom);
    const added = grids.filter(
      (grid) => known[String(grid.structureGridId)] === undefined
    );
    if (added.length === 0) {
      return;
    }
    set(knownGridsAtom, {
      ...known,
      ...Object.fromEntries(
        added.map((grid) => [String(grid.structureGridId), grid])
      ),
    });
  }
);

/** The grids on the section. An empty list is "none added", not "all". */
export const sectionGridIdsAtom = atom<number[]>([]);

/**
 * The rail's colour-by rules for Structure Grids, written over grid metadata rather than
 * a per-grid list, so adding a grid re-derives the values. Display state, so nothing
 * refetches; held with the section so the plot and rail cannot disagree.
 */
export const gridColorRulesAtom = atom<SectionColorRule[]>([]);

/**
 * The rail's colour-by rules for Wells — over the attributes the cut carries per
 * crossing. The section does not invent what a well colour means; this is where that
 * meaning is set, beside the grids' rule.
 */
export const wellColorRulesAtom = atom<SectionColorRule[]>([]);

/**
 * The rail's filters for Wells, over the same attributes the colour rule uses. They
 * narrow which crossings take part, and survive redrawing the line: a filter is a
 * question about wells, not about this particular cut.
 */
export const wellFiltersAtom = atom<WellFilter[]>([]);

/**
 * The window the plot is zoomed to, when it is worth asking the backend to re-sample
 * it. Null means the request covers the whole line.
 */
export const refineViewportAtom = atom<SectionViewport | null>(null);

/**
 * What the whole line reported about how finely it can be sampled, kept from the last
 * unwindowed cut. The decision to refine has to be made against this rather than
 * against a refined answer, which reports the step it was handed.
 */
export const refineBaselineAtom = atom<RefineBaseline | null>(null);

/**
 * The grid the section is hung on, or null for structural hanging. Selecting the
 * active datum again clears it, as does removing that grid from the section.
 */
export const sectionDatumGridIdAtom = atom<number | null>(null);

/**
 * Puts a collection on the section, with its whole stack on the plot. Switching
 * collection replaces the stack rather than merging: the grids are the collection's,
 * and a datum from the old one would be hanging the section on a grid that is no
 * longer there.
 *
 * @param selection - The collection and the grid ids it brings.
 */
export const selectCollectionAtom = atom(
  null,
  (
    get,
    set,
    {
      collectionId,
      gridIds,
      automatic = false,
    }: {
      collectionId: number;
      gridIds: number[];
      /**
       * True when the section picked this for itself. Such a choice is a guess and stays
       * correctable; anything else is the user's instruction and is not.
       */
      automatic?: boolean;
    }
  ) => {
    if (get(structureGridCollectionIdAtom) === collectionId) {
      return;
    }
    if (!automatic) {
      set(collectionChosenByUserAtom, true);
    }
    set(structureGridCollectionIdAtom, collectionId);
    set(sectionGridIdsAtom, gridIds);
    set(sectionDatumGridIdAtom, null);
  }
);

/**
 * Takes a grid off the section. A datum on that grid goes with it — the section
 * cannot hang from a surface it is no longer sampling.
 *
 * @param gridId - The grid to remove.
 */
export const removeSectionGridAtom = atom(null, (get, set, gridId: number) => {
  set(
    sectionGridIdsAtom,
    get(sectionGridIdsAtom).filter((id) => id !== gridId)
  );
  if (get(sectionDatumGridIdAtom) === gridId) {
    set(sectionDatumGridIdAtom, null);
  }
});

/**
 * Adds grids to the section, keeping the stack in the order it already had and
 * ignoring any that are on it already.
 *
 * @param gridIds - The grids to add.
 */
export const addSectionGridsAtom = atom(null, (get, set, gridIds: number[]) => {
  const current = get(sectionGridIdsAtom);
  const added = gridIds.filter((id) => !current.includes(id));
  set(sectionGridIdsAtom, [...current, ...added]);
});

/**
 * Hangs the section on a grid, or takes it off the one it is on. Flattening is a
 * toggle: pressing the active datum again is how the section goes back to being
 * structural, which is what makes the same control the way out.
 *
 * @param gridId - The grid to flatten on.
 */
export const toggleDatumGridAtom = atom(null, (get, set, gridId: number) => {
  set(
    sectionDatumGridIdAtom,
    get(sectionDatumGridIdAtom) === gridId ? null : gridId
  );
});

// ── Display state — none of this refetches ──────────────────────────────────

/** Which depth the axis reads. `flattened` is implied by a datum being set. */
export type DepthReference = 'tvdss' | 'tvd';

export const depthReferenceAtom = atom<DepthReference>('tvdss');

/** Distance along the section, in feet or miles. */
export const distanceUnitAtom = atom<'ft' | 'mi'>('ft');

export const showGroundSurfaceAtom = atom(false);

/**
 * Whether the calculated intersections have been asked for at all. Sticky for the life
 * of the section: the first ask is a calculation and every plane edit recalculates, but
 * hiding is a glance — so the switches below only decide whether the answer is drawn.
 */
export const wellResultsRequestedAtom = atom(false);
export const faultResultsRequestedAtom = atom(false);
export const showIntervalFillsAtom = atom(false);
export const showLabelsAtom = atom(true);
export const showLocationIndicatorAtom = atom(false);

/**
 * Dragging an axis gutter rescales just that axis, as the Inventory gun barrel chart does.
 *
 * Off by default, and set only by the Wellbore Geometry tearsheet's own store — this chart is
 * shared with the Map View's cross-section drawer, which keeps the gestures it has today.
 */
export const axisZoomEnabledAtom = atom(false);

/**
 * The position both views point at, as a distance along the section, or null when
 * neither does. One value rather than one per view: a hover on either sets it and both
 * read it back, so they cannot track separate ideas of where the pointer is.
 */
export const hoveredDistanceFtAtom = atom<number | null>(null);
export const showWellResultsAtom = atom(true);
export const showFaultResultsAtom = atom(true);
export const sectionFlippedAtom = atom(false);

/**
 * Bumped to put the plot back to the extent the section opened at. A counter rather
 * than a flag: two resets in a row are two resets, and a flag would swallow the
 * second.
 */
export const chartViewResetAtom = atom(0);

/** Restages the plot without touching what the section is. */
export const resetChartViewAtom = atom(null, (get, set) => {
  set(chartViewResetAtom, get(chartViewResetAtom) + 1);
});

/**
 * Crop the axis to where grid data exists, rather than spanning the whole drawn
 * line. Held separately from the mode so switching to Well to Well — where cropping
 * is unavailable — and back does not lose what the user had set in Arbitrary Line.
 */
export const cropToGridExtentsAtom = atom(false);

/** The focused well's uwi, emphasised on the profile, the map and the list at once. */
export const focusedWellUwiAtom = atom<string | null>(null);

// ── Panels ──────────────────────────────────────────────────────────────────

/** The right-hand Cross Section Settings panel. */
export const settingsPanelOpenAtom = atom(false);

/** The map's layers panel. Never open at the same time as settings. */
export const layersPanelOpenAtom = atom(false);

export const leftRailCollapsedAtom = atom(false);

/**
 * Opens settings and closes the layers panel in one write. The two are mutually
 * exclusive — both open would leave the map covered from both sides — so the rule
 * lives here rather than in whichever component happened to be clicked.
 */
export const openSettingsPanelAtom = atom(null, (_get, set) => {
  set(settingsPanelOpenAtom, true);
  set(layersPanelOpenAtom, false);
});

/** The mirror of the above: opening layers closes settings. */
export const openLayersPanelAtom = atom(null, (_get, set) => {
  set(layersPanelOpenAtom, true);
  set(settingsPanelOpenAtom, false);
});

// ── Lifecycle writes ────────────────────────────────────────────────────────

/**
 * Clears the geometry and everything calculated against it, leaving the tool armed and
 * asking for a mode — the same question the arm button asks. Display preferences
 * survive: the user asked to be rid of this section, not to reset how sections look.
 */
export const clearSectionAtom = atom(null, (get, set) => {
  set(sectionModeAtom, null);
  set(wellResultsRequestedAtom, false);
  set(faultResultsRequestedAtom, false);
  // The question goes back to the map, beside the tool button. The drawer stays
  // open and says where to look rather than asking again: the same choice offered
  // twice is two things to dismiss.
  set(sectionModeMenuOpenAtom, true);
  set(sectionVerticesAtom, []);
  set(sectionWellsAtom, []);
  set(sectionGenerationAtom, get(sectionGenerationAtom) + 1);
  set(sectionOrderingAtom, 'as_selected');
  set(sectionDatumGridIdAtom, null);
  set(focusedWellUwiAtom, null);
  set(refineViewportAtom, null);
  set(refineBaselineAtom, null);
});

/**
 * Switches construction method, discarding the current line — the two modes cannot
 * share a plane, and a click means something different in each.
 *
 * @param mode - The method to switch to.
 */
export const setSectionModeAtom = atom(
  null,
  (get, set, mode: SectionMode | null) => {
    if (get(sectionModeAtom) === mode) {
      return;
    }
    set(sectionVerticesAtom, []);
    set(sectionWellsAtom, []);
    set(sectionGenerationAtom, get(sectionGenerationAtom) + 1);
    set(sectionOrderingAtom, 'as_selected');
    set(focusedWellUwiAtom, null);
    set(sectionModeAtom, mode);
  }
);

/**
 * Puts the tool away: the drawer closes, the section goes, and the map stops taking
 * section clicks. Pressing the arm button again while armed lands here.
 *
 * The rail goes with it. Its rules and filters reach the map's own wells while the tool
 * is open, so leaving them set would hand the user back a recoloured, narrowed base map
 * with nothing on screen to explain it — and no way to undo it but to arm the tool again.
 * Arming next time seeds the defaults afresh, which is what back-to-default means here.
 */
export const disarmCrossSectionAtom = atom(null, (get, set) => {
  set(crossSectionArmedAtom, false);
  set(wellResultsRequestedAtom, false);
  set(faultResultsRequestedAtom, false);
  set(sectionModeMenuOpenAtom, false);
  set(sectionModeAtom, null);
  set(sectionVerticesAtom, []);
  set(sectionWellsAtom, []);
  set(sectionGenerationAtom, get(sectionGenerationAtom) + 1);
  set(sectionOrderingAtom, 'as_selected');
  set(sectionDatumGridIdAtom, null);
  set(focusedWellUwiAtom, null);
  set(settingsPanelOpenAtom, false);
  set(collectionChosenByUserAtom, false);
  set(gridColorRulesAtom, []);
  set(wellColorRulesAtom, []);
  set(wellFiltersAtom, []);
});

/**
 * Singles a well out, or clears the focus by picking the focused one again.
 *
 * One well at a time, and the same well wherever it appears: the plot, the map and
 * the list all read this, so picking on any of them moves the one emphasis.
 *
 * @param uwi - The well picked.
 */
export const toggleFocusedWellAtom = atom(null, (get, set, uwi: string) => {
  set(focusedWellUwiAtom, get(focusedWellUwiAtom) === uwi ? null : uwi);
});

/**
 * Turns a calculated dataset on: the first press asks for it and draws it, and every
 * press after that only decides whether it is drawn.
 *
 * @param dataset - Which calculated dataset the press was for.
 */
export const toggleResultsAtom = atom(
  null,
  (get, set, dataset: 'wells' | 'faults') => {
    const requestedAtom =
      dataset === 'wells'
        ? wellResultsRequestedAtom
        : faultResultsRequestedAtom;
    const visibleAtom =
      dataset === 'wells' ? showWellResultsAtom : showFaultResultsAtom;

    if (!get(requestedAtom)) {
      set(requestedAtom, true);
      set(visibleAtom, true);
      return;
    }
    set(visibleAtom, !get(visibleAtom));
  }
);

/**
 * Adds a well to the section, or takes it off again — clicking a picked well is how it
 * is removed, which is the same gesture that added it.
 *
 * @param well - The well under the pointer.
 */
export const toggleSectionWellAtom = atom(
  null,
  (get, set, well: PickedWell) => {
    const current = get(sectionWellsAtom);
    const picked = current.some((candidate) => candidate.uwi === well.uwi);
    set(
      sectionWellsAtom,
      picked
        ? current.filter((candidate) => candidate.uwi !== well.uwi)
        : [...current, well]
    );
  }
);

/**
 * Asks which method to use, from beside the arm button. Pressing the button again
 * while the prompt is open shuts it: the prompt has no scrim, so the button stays
 * clickable underneath, and reopening it would look like nothing had happened.
 */
export const toggleSectionModeMenuAtom = atom(null, (get, set) => {
  set(sectionModeMenuOpenAtom, !get(sectionModeMenuOpenAtom));
});

/**
 * A method was picked, so the section starts: the prompt closes and the drawer opens
 * on an empty plane, with the map now taking clicks.
 *
 * @param mode - The method to build with.
 */
export const startSectionAtom = atom(null, (get, set, mode: SectionMode) => {
  set(sectionModeMenuOpenAtom, false);
  set(crossSectionArmedAtom, true);
  set(sectionVerticesAtom, []);
  set(sectionWellsAtom, []);
  set(sectionGenerationAtom, get(sectionGenerationAtom) + 1);
  set(sectionOrderingAtom, 'as_selected');
  set(focusedWellUwiAtom, null);
  set(sectionModeAtom, mode);
});

// ── Availability ────────────────────────────────────────────────────────────

/**
 * How many maps currently have the tool mounted. A count rather than a flag so a
 * remount — React strict mode, or a page swapping map instances — cannot leave the
 * control stack showing a button for a map that has gone.
 */
const crossSectionMountCountAtom = atom(0);

/** Registers a mounted tool; the cleanup passes false. */
export const registerCrossSectionToolAtom = atom(
  null,
  (get, set, mounted: boolean) => {
    const next = Math.max(
      0,
      get(crossSectionMountCountAtom) + (mounted ? 1 : -1)
    );
    set(crossSectionMountCountAtom, next);
    // The last map carrying the tool has gone, so there is nothing left to draw on.
    if (next === 0) {
      set(crossSectionArmedAtom, false);
      set(sectionModeMenuOpenAtom, false);
      set(sectionModeAtom, null);
      set(sectionVerticesAtom, []);
      set(sectionWellsAtom, []);
      set(sectionGenerationAtom, get(sectionGenerationAtom) + 1);
    }
  }
);
